/**
 * 천장 패널 수정 창
 * - 방향(가로/세로)·흐름(결합 방향)은 천장 전체에 적용 — 결합 음양수는 흐름으로 자동
 * - 사이즈: 이 패널을 고정 → 나머지가 자동으로 메움 (메울 패널 없으면 추가), 바꾸는 즉시 미리보기
 */
import React, { useEffect, useState } from 'react';
import { CeilingPanel } from '../types';
import { FillSpec, SegRule, defaultRule } from '../utils/segmentFill';
import { CeilingFlow } from '../utils/ceilingLayout';
import { selectAllOnFocus, useDragOffset } from './dialogKit';

interface Props {
  panel: CeilingPanel;
  count: number;
  direction: 'H' | 'V';
  flow: CeilingFlow;
  rule: SegRule | null;          // 현재 확정 규칙
  spec: FillSpec;
  previewError?: string | null;
  onPreview: (rule: SegRule | null) => void;
  onApply: (rule: SegRule | null) => void;
  onDirection: (dir: 'H' | 'V') => void;
  onFlow: (flow: CeilingFlow) => void;
  edge: number | null;           // 첫 패널(벽 위) 사이즈 — 켜져 있으면 그 패널은 고정, 규칙은 다음 패널부터
  edgeSizeIfOn: number;
  onEdge: (on: boolean) => void;
  onOpenSegment: () => void;
  onCancel: () => void;
}

const TYPE_NAME = { yangsu: '양수', eumsu: '음수', none: '없음 (벽에 닿음)' } as const;

const CeilingPanelEditDialog: React.FC<Props> = ({ panel, count, direction, flow, rule, spec, previewError, onPreview, onApply, onDirection, onFlow, edge, edgeSizeIfOn, onEdge, onOpenSegment, onCancel }) => {
  const [size, setSize] = useState(String(panel.size));
  const drag = useDragOffset();
  const i = panel.index;
  // 가로 천장은 패널이 위→아래로 쌓임, 세로 천장은 왼→오
  const [loName, hiName] = direction === 'H' ? ['위', '아래'] : ['왼쪽', '오른쪽'];
  const flowLabel = (f: CeilingFlow) => (direction === 'H' ? (f === 'lo' ? '↑ 위로' : '↓ 아래로') : (f === 'lo' ? '← 왼쪽으로' : '→ 오른쪽으로'));

  const isEdge = !!edge && i === 0;            // 벽 위 첫 패널 (두께로 결정, 수정 불가)
  const lockNo = i + 1 - (edge ? 1 : 0);       // 규칙 안 순번 (첫 패널 다음부터 1번)
  const sizeNum = parseInt(size);
  const sizeErr = isEdge ? null : isNaN(sizeNum) ? '숫자를 입력하세요'
    : sizeNum > spec.max ? `최대 ${spec.max}mm — 제작 불가` : sizeNum < spec.min ? `최소 ${spec.min}mm` : null;
  const build = (): SegRule | null => {
    if (isEdge || sizeErr || sizeNum === panel.size) return rule;
    const base = rule ?? defaultRule(spec);
    return { ...base, locks: { ...base.locks, [lockNo]: sizeNum } };
  };
  useEffect(() => { onPreview(build()); }, [size]); // eslint-disable-line react-hooks/exhaustive-deps

  const err = sizeErr ?? previewError ?? null;
  const label: React.CSSProperties = { display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 'bold', color: '#555' };
  const btn = (active: boolean): React.CSSProperties => ({
    flex: 1, padding: '7px 4px', fontSize: 13, cursor: 'pointer', borderRadius: 6,
    border: active ? '2px solid #3182F6' : '1px solid #E5E8EB', background: active ? '#E8F3FF' : '#fff', fontWeight: active ? 'bold' : 'normal',
  });
  return (
    <div style={{ position: 'fixed', top: 90, right: 24, width: 340, zIndex: 1000, background: '#fff', borderRadius: 20, overflow: 'hidden',
      boxShadow: '0 12px 40px rgba(0,0,0,0.16)', transform: `translate(${drag.offset.x}px, ${drag.offset.y}px)` }}>
      <div onMouseDown={drag.onMouseDown} style={{ background: '#fff', color: '#191F28', padding: '16px 20px 8px', borderRadius: 0,
        cursor: 'move', userSelect: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 'bold', fontSize: 14 }}>천장 패널 수정 — {i + 1}/{count}번</span>
        <button onClick={onCancel} style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: '#F2F4F6', color: '#4E5968', fontSize: 14, cursor: 'pointer', flexShrink: 0 }}>✕</button>
      </div>
      <div style={{ padding: 16 }}>
        <label style={label}>패널 방향 (천장 전체)</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {(['H', 'V'] as const).map(d => (
            <button key={d} onClick={() => d !== direction && onDirection(d)} style={btn(direction === d)}>{d === 'H' ? '가로' : '세로'}</button>
          ))}
        </div>

        <label style={label}>결합 흐름 (천장 전체 — 음양수 자동)</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
          {(['lo', 'hi'] as const).map(f => (
            <button key={f} onClick={() => f !== flow && onFlow(f)} style={btn(flow === f)}>{flowLabel(f)}</button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: '#999', marginBottom: 12 }}>흐름 쪽 끝이 양수로 다음 패널 음수에 꽂힘 · 벽에 닿는 양 끝은 없음</div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#555', marginBottom: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!edge} onChange={e => onEdge(e.target.checked)} />
          첫 패널(벽 위에 걸침) {edgeSizeIfOn} 사용 — 두께별 규격
        </label>

        <label style={label}>패널 사이즈 (mm){isEdge ? ' — 벽 위 첫 패널 (두께로 고정)' : ''}</label>
        <input type="number" value={size} disabled={isEdge} onChange={e => setSize(e.target.value)} onFocus={selectAllOnFocus} autoFocus
          min={spec.min} max={spec.max} step={10}
          style={{ width: '100%', padding: '6px 10px', fontSize: 13, borderRadius: 8, boxSizing: 'border-box', border: `1px solid ${err ? '#F04452' : '#ddd'}` }} />
        <div style={{ fontSize: 11, marginTop: 3, marginBottom: 10, color: err ? '#F04452' : '#999', fontWeight: err ? 'bold' : 'normal' }}>
          {err ? `⚠ ${err}` : '바꾸면 나머지 패널이 자동으로 길이를 맞춤'}
        </div>

        <div style={{ display: 'flex', gap: 8, fontSize: 12, color: '#555', background: '#f5f5f5', borderRadius: 6, padding: '6px 10px' }}>
          <span style={{ flex: 1 }}>{loName} 끝: <b>{TYPE_NAME[panel.leftType as keyof typeof TYPE_NAME]}</b></span>
          <span style={{ flex: 1 }}>{hiName} 끝: <b>{TYPE_NAME[panel.rightType as keyof typeof TYPE_NAME]}</b></span>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button onClick={onOpenSegment} style={{ marginRight: 'auto', padding: '7px 12px', fontSize: 13, border: '1px solid #3182F6', borderRadius: 8,
            background: '#fff', color: '#3182F6', cursor: 'pointer', fontWeight: 'bold' }}>📐 전체 채우기</button>
          <button onClick={onCancel} style={{ padding: '7px 16px', fontSize: 13, border: '1px solid #E5E8EB', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>취소</button>
          <button onClick={() => onApply(build())} disabled={!!err}
            style={{ padding: '7px 16px', fontSize: 13, border: 'none', borderRadius: 8, background: '#3182F6', color: '#fff', fontWeight: 'bold',
              cursor: err ? 'not-allowed' : 'pointer', opacity: err ? 0.4 : 1 }}>적용</button>
        </div>
      </div>
    </div>
  );
};

export default CeilingPanelEditDialog;
